// Catalog.ID Client — Chat-Request Mode Handlers
// ============================================================================
// Per-mode dispatch for chat-originated `registry-request` envelopes. The
// dispatcher in `client.ts` routes here based on
// `request.requesterId === 'chat'`.
//
// Inbound envelopes have already been signature-verified by the cld-id
// server's relay. Browser code re-verifies only the body↔canonical binding
// (the cids digest) — the signature covers the digest, the body carries the
// list, so a relay-verified envelope cannot be paired with a substituted
// cids list.
//
// v1 modes:
//   - `get-claims`: see chat-types.ts. Lock-degrading (public-only when the
//     keybag is locked), never interactive.
//   - `issue-cert`: chat_sign sub-key certification — see
//     `chat-cert-handlers.ts`. The opposite lock posture: ceremony-grade, so
//     it WAITS for an unlock rather than degrading.
//   - `decap-chat-epoch`: the chat epoch oracle hop — see
//     `chat-epoch-handlers.ts`. Non-interactive (nothing needs the tab raised
//     BEFORE delivery, because no dialog is coming) but, since M2c, no longer
//     fail-fast: a locked bag raises the tab from the handler and waits.
//   - `create-chat-address`: create + share the `chat_address` consent record —
//     see `chat-address-handlers.ts`. A fourth posture: ceremony-grade AND
//     consent-gated, because it is the only chat mode that writes durable
//     account state and hands a named peer a key to it.
// ============================================================================

import { keys } from '../state/keys';
import { sha512 } from '@noble/hashes/sha2.js';
import * as aes from '../crypto/aes';
import { handleIssueCert } from './chat-cert-handlers';
import { handleDecapChatEpoch } from './chat-epoch-handlers';
import { handleCreateChatAddress } from './chat-address-handlers';
import type { ChatRequest, ChatResponse, ResolvedClaim, ChatDispatchDeps } from './chat-types';

type ResponseBase = Pick<ChatResponse, 'type' | 'to' | 'requestId'>;

/** Batch cap — chat resolves conversations incrementally; anything larger is
 *  either a bug or an attempt to bulk-harvest shared claims. */
const MAX_CIDS = 50;

/** Digest convention shared with the chat service's canonical builder:
 *  SHA-512 over the UTF-8 bytes of the ordinal-sorted, comma-joined cids,
 *  base64-encoded. CIDs are 10-char lowercase alphanumerics, so the comma
 *  separator is unambiguous and JS default sort matches C# ordinal sort. */
export function computeCidsDigest(cids: string[]): string {
    const canonical = [...cids].sort().join(',');
    return aes.toBase64(sha512(new TextEncoder().encode(canonical)));
}

/** Re-exported from `chat-types` (where it lives to keep chat-handlers ↔
 *  chat-cert-handlers acyclic) so existing importers keep working. */
export type { ChatDispatchDeps };

export async function dispatchChatRequest(
    request: ChatRequest,
    deps: ChatDispatchDeps,
): Promise<ChatResponse> {
    const base: ResponseBase = {
        type: 'registry-response',
        to: request.requesterId,
        requestId: request.requestId,
    };

    if (!request._senderServiceId || request._senderServiceId !== request.requesterId) {
        return { ...base, status: 'unavailable', reason: 'sender-mismatch' };
    }
    if (request.requesterId !== 'chat') {
        return { ...base, status: 'unavailable', reason: 'unsupported-requester' };
    }

    const viewer = deps.getActiveCid();
    if (!viewer) return { ...base, status: 'unavailable', reason: 'no-active-identity' };

    switch (request.mode) {
        case 'get-claims':
            return await handleGetClaims(request, base, deps);
        case 'issue-cert':
            return await handleIssueCert(request, base, viewer, deps);
        case 'decap-chat-epoch':
            return await handleDecapChatEpoch(request, base, deps);
        case 'create-chat-address':
            return await handleCreateChatAddress(request, base, viewer, deps);
        default:
            return { ...base, status: 'unavailable', reason: 'unsupported-mode' };
    }
}

async function handleGetClaims(
    request: ChatRequest,
    base: ResponseBase,
    deps: ChatDispatchDeps,
): Promise<ChatResponse> {
    const cids = request.cids;
    if (!Array.isArray(cids) || cids.length === 0 || !request.cidsDigestBase64) {
        return { ...base, status: 'unavailable', reason: 'missing-required-field' };
    }
    if (cids.length > MAX_CIDS) {
        return { ...base, status: 'unavailable', reason: 'too-many-cids' };
    }
    // Body↔canonical binding: the relay's signature verification covered the
    // digest; make sure the body's list is the one that was signed.
    if (computeCidsDigest(cids) !== request.cidsDigestBase64) {
        return { ...base, status: 'unavailable', reason: 'cids-digest-mismatch' };
    }

    const unique = [...new Set(cids)];
    const unlocked = keys.isLoaded();

    // Shared-with-viewer claims need the unlocked attr_encrypt key. This is a
    // background display refresh — degrade to public-only rather than wait or
    // steal focus; chat retries after the user unlocks.
    //
    // THE ONE DELIBERATE EXCEPTION to the universal unlock redirect (M2c,
    // §10 item 5: "any request to CID while the key bag is locked must raise
    // the CID tab and wait"). `get-claims` is a periodic roster refresh with
    // no member behind it, so making it raise the tab would be the exact
    // failure the hub's own header records — a background poll yanking the
    // dashboard between tabs. It answers `partial: 'cid-locked'` with the
    // public half instead, which is a useful answer, not a refusal.
    let sharedByOwner = new Map<string, ResolvedClaim[]>();
    if (unlocked) {
        const shared = await deps.getDecryptedSharedClaims();
        sharedByOwner = shared.reduce((map, s) => {
            if (!map.has(s.ownerCid)) map.set(s.ownerCid, []);
            map.get(s.ownerCid)!.push({ type: s.type, value: s.value, shared: true });
            return map;
        }, new Map<string, ResolvedClaim[]>());
    }

    const claims: Record<string, ResolvedClaim[]> = {};
    await Promise.all(unique.map(async (cid) => {
        let publicClaims: ResolvedClaim[] = [];
        try {
            const profile = await deps.getPublicProfile(cid);
            publicClaims = profile.claims.map(c => ({ type: c.claimType, value: c.publicValue }));
        } catch {
            // Unknown cid / transport failure — resolve to empty so the caller
            // can cache the negative instead of retrying forever.
        }
        claims[cid] = [...publicClaims, ...(sharedByOwner.get(cid) ?? [])];
    }));

    return {
        ...base,
        status: 'approved',
        claims,
        ...(unlocked ? {} : { partial: 'cid-locked' }),
    };
}
